import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ContextoRadar } from '@/components/radar/ContextoRadar';
import { ProcessoChatPreview, type ProcessoChatPreviewItem } from '@/components/radar/ProcessoChatPreview';
import { AndamentoDoProcesso } from '@/components/radar/AndamentoDoProcesso';
import { useRadarTimeline } from '@/hooks/useRadar';
import { ETAPA_LABELS } from '@/lib/pipelineKanban';

interface LicitacaoDetalhesEntrada {
  /** `empresa_licitacoes_pipeline.id` — é o `pipelineId` do Radar. */
  id?: number;
  empresaId: number;
  empresaNome: string;
  licitacaoId: number;
  etapa: string;
  observacoes: string | null;
  licitacao: {
    objeto: string;
    orgao: string | null;
    uf: string | null;
    valorEstimado: number | null;
    prazoAbertura: string | null;
    modalidade: string | null;
  };
}

/** Polling mais folgado: a visão da equipe é só leitura e não tem SSE. */
const INTERVALO_STAFF_MS = 60_000;

interface LicitacaoDetalhesModalProps {
  entrada: LicitacaoDetalhesEntrada | null;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('pt-BR');

/**
 * Visão read-only do andamento para a equipe. Só é montada quando o chat é
 * aberto, e cai no placeholder honesto quando a licitação não tem
 * monitoramento conectado. A sonda compartilha a chave de cache com o
 * AndamentoDoProcesso, então não há requisição extra.
 */
function ChatDoProcessoStaff({
  pipelineId,
  empresaId,
  placeholder,
  onClose,
}: {
  pipelineId: number;
  empresaId: number;
  placeholder: ProcessoChatPreviewItem;
  onClose: () => void;
}) {
  const andamento = useRadarTimeline({ pipelineId, empresaId, intervaloMs: INTERVALO_STAFF_MS });

  if (andamento.isError || andamento.data?.semMonitoramento) {
    return <ProcessoChatPreview item={placeholder} onClose={onClose} />;
  }

  return (
    <Modal isOpen onClose={onClose} title="Chat do processo" contentClassName="max-w-4xl">
      {andamento.isLoading ? (
        <p role="status" className="text-sm text-ds-text-secondary">Carregando o andamento do processo...</p>
      ) : (
        <AndamentoDoProcesso
          pipelineId={pipelineId}
          empresaId={empresaId}
          somenteLeitura
          tempoReal={false}
          intervaloPollingMs={INTERVALO_STAFF_MS}
          id={`andamento-staff-${pipelineId}`}
        />
      )}
    </Modal>
  );
}

function LicitacaoDetalhesModal({ entrada, onClose }: LicitacaoDetalhesModalProps) {
  const [showContext, setShowContext] = useState(false);
  const [showChat, setShowChat] = useState(false);
  if (!entrada) return null;

  const pipelineId = entrada.id || 0;
  const placeholderChat: ProcessoChatPreviewItem = {
    id: entrada.licitacaoId,
    titulo: entrada.licitacao.objeto,
    orgao: entrada.licitacao.orgao,
    fonte: null,
  };

  return (
    <Modal isOpen onClose={onClose} title="Detalhes da licitação">
      <div className="space-y-5">
        <div>
          <h4 className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide mb-2">Cliente</h4>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-ds-border bg-ds-surface2 p-3">
            <span className="text-sm font-medium text-ds-text-primary">{entrada.empresaNome}</span>
            <Link
              to={`/empresas/${entrada.empresaId}/perfil-tecnico`}
              className="text-xs font-medium text-ds-success hover:underline shrink-0"
            >
              Ver perfil do cliente
            </Link>
          </div>
        </div>

        <section className="rounded-lg border border-ds-success/25 bg-ds-success/5 p-3" aria-label="Radar do processo">
          <p className="text-sm font-semibold text-ds-text-primary">Radar do processo</p>
          <p className="mt-1 text-sm text-ds-text-secondary">Esta licitação já está em gestão. Antes de avançar, confira o edital, o prazo e a documentação exigida.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowContext((current) => !current)}>{showContext ? 'Ocultar contexto' : 'Ver contexto'}</Button>
            <Button size="sm" variant="secondary" className="gap-2" onClick={() => setShowChat(true)}><MessageSquare className="h-4 w-4" />Chat do processo</Button>
          </div>
          {showContext && <ContextoRadar prazo={entrada.licitacao.prazoAbertura ? formatDate(entrada.licitacao.prazoAbertura) : null} />}
          <p className="mt-3 text-xs text-ds-text-secondary"><strong>Importante:</strong> boa aderência não significa habilitação confirmada. Confira o edital e seus documentos.</p>
        </section>

        <div>
          <h4 className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide mb-2">Licitação</h4>
          <div className="rounded-lg border border-ds-border bg-ds-surface2 p-3 space-y-2">
            <p className="text-sm font-medium text-ds-text-primary">{entrada.licitacao.objeto}</p>
            {entrada.licitacao.orgao && (
              <p className="text-xs text-ds-text-secondary">{entrada.licitacao.orgao}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{(ETAPA_LABELS as Record<string, string>)[entrada.etapa] || entrada.etapa}</Badge>
              {entrada.licitacao.uf && <Badge size="sm">{entrada.licitacao.uf}</Badge>}
              {entrada.licitacao.modalidade && <Badge size="sm">{entrada.licitacao.modalidade}</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              {entrada.licitacao.valorEstimado != null && (
                <div>
                  <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Valor estimado</p>
                  <p className="text-sm font-semibold text-ds-success">
                    {formatCurrency(Number(entrada.licitacao.valorEstimado))}
                  </p>
                </div>
              )}
              {entrada.licitacao.prazoAbertura && (
                <div>
                  <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Prazo de abertura</p>
                  <p className="text-sm font-medium text-ds-text-primary">
                    {formatDate(entrada.licitacao.prazoAbertura)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {entrada.observacoes && (
          <div>
            <h4 className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide mb-2">Observações</h4>
            <p className="text-sm text-ds-text-primary whitespace-pre-wrap">{entrada.observacoes}</p>
          </div>
        )}
      </div>
      {showChat && pipelineId > 0 ? (
        <ChatDoProcessoStaff
          pipelineId={pipelineId}
          empresaId={entrada.empresaId}
          placeholder={placeholderChat}
          onClose={() => setShowChat(false)}
        />
      ) : (
        <ProcessoChatPreview item={showChat ? placeholderChat : null} onClose={() => setShowChat(false)} />
      )}
    </Modal>
  );
}

export { LicitacaoDetalhesModal };
